// Context-aware starter prompts for HelpChat.
//
// Selection is deterministic and client-side — no API call, no latency, and we
// keep full control of the wording (YMYL phrasing matters). Priority order:
//   1. Builder open  -> stage/missing-aware prompts about THIS draft
//   2. Page published its own (e.g. a template's FAQ questions)
//   3. Route-keyed bank
//   4. Generic fallback
//
// Follow-up chips shown AFTER an answer come from the model (see _worker.js), not
// from here — this file only seeds the empty-state and per-page prompts.

import type { ChatContext } from "./pageContext";

export const GENERIC_STARTERS = ["What is a RAMS?", "How does the builder work?", "Is it really free?"];

// Longest-prefix match wins, so put specific routes before their parents.
const ROUTE_STARTERS: { prefix: string; starters: string[] }[] = [
  { prefix: "/templates", starters: ["Which template fits my job?", "How do I make a template site-specific?", "Are the templates really free?"] },
  { prefix: "/categories", starters: ["Which RAMS do I need for this work?", "What's the difference between these templates?", "How do I build one of these?"] },
  { prefix: "/trades", starters: ["Which RAMS do I need for this trade?", "Can I edit the controls for my job?", "How long does the builder take?"] },
  { prefix: "/tools/rams-rejection-checker", starters: ["Why do RAMS get rejected?", "What does the checker look for?", "How do I fix a rejected RAMS?"] },
  { prefix: "/tools/rams-checker", starters: ["What does the pre-submission checker do?", "What do principal contractors check?", "Is my RAMS ready to submit?"] },
  { prefix: "/tools", starters: ["What does this tool do?", "Is it free to use?", "How does this fit with the RAMS builder?"] },
  { prefix: "/guides", starters: ["How do I write a RAMS?", "What must a RAMS include?", "Who has to sign off a RAMS?"] },
  { prefix: "/builder", starters: ["How does the builder work?", "What do I need to fill in?", "How do I download my RAMS?"] },
];

function routeStarters(path: string): string[] | null {
  let best: { prefix: string; starters: string[] } | null = null;
  for (const r of ROUTE_STARTERS) {
    if (path.startsWith(r.prefix) && (!best || r.prefix.length > best.prefix.length)) best = r;
  }
  return best ? best.starters : null;
}

function builderStarters(ctx: ChatContext): string[] {
  const b = ctx.builder!;
  if (b.stage === "choosing") {
    return ["Which report should I pick?", "How do I make this site-specific?", `What does the ${b.report} RAMS cover?`];
  }
  if (b.emailGated) {
    return ["Why do I need to enter an email?", "Is my download watermarked?", "Is this really free?"];
  }
  if (b.missing.length) {
    // Lead with the actual outstanding item so the prompt is about THEIR draft.
    const first = b.missing[0].split(" - ")[0].toLowerCase();
    return [`What's left before I can submit?`, `How do I fix: ${first}?`, "Can I download an incomplete RAMS?"];
  }
  return [`Is my ${b.report} RAMS ready to submit?`, "How do I edit the controls?", "What do principal contractors check?"];
}

/** Pick three starter prompts for the current context. Always returns three. */
export function pickStarters(ctx: ChatContext): string[] {
  if (ctx.builder) return builderStarters(ctx);
  if (ctx.pageStarters && ctx.pageStarters.length) return ctx.pageStarters.slice(0, 3);
  return routeStarters(ctx.path) || GENERIC_STARTERS;
}
